import './DeliveryOptions.scss';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import {
  forwardRef,
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useMemo,
} from 'react';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {ArrToggle} from '../../../common/arr-toggle/ArrToggle';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import ArrSingleSelectDropdown from '../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrModal from '../../../common/arr-modal/ArrModal';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {ISelectItem} from '../../../../shared/types';
import {
  deliveryOptionsSchema,
  DeliveryOptionsFormData,
} from '../../../../schemas/productions/productionSchema';
import {
  ToastType,
  FormMode,
  ModuleType,
  FormAction,
  ProductionStatus,
} from '../CreateNewProductionUtils';
import {
  useGetProductLocationView,
  useValidateDestination,
  useCreateDestination,
  useSaveProductionLocation,
} from '../../../../hooks/productions/useProductions';
import {setIsFieldsChanged} from '../../../../redux/slice/productionSlice';

export interface StepChildRef {
  save: () => void;
  next: () => void;
}

interface DeliveryOptionsProps {
  productionId: number;
  isProductionSet: boolean;
  exportProductionId?: number;
  getStatus: (showToast: boolean, status: ToastType, message?: string) => void;
  goNext?: () => void;
  goBack?: () => void;
  mode?: FormMode;
  onStateChange?: (state: {
    saveDisabled: boolean;
    nextDisabled: boolean;
  }) => void;
  skipToDocumentSelection?: () => void;
  productionStatusCode?: string;
  isUpdateNeeded?: boolean;
  onUpdateNeededChange?: (value: boolean) => void;
}

type SubmissionContext = {
  formData: DeliveryOptionsFormData;
  markComplete: boolean;
  rootPathId: number;
  rootPathName: string;
};

type FolderModalState = {
  show: boolean;
  message: string;
  context: SubmissionContext | null;
};

const DeliveryOptionsInner: ForwardRefRenderFunction<
  StepChildRef,
  DeliveryOptionsProps
> = (
  {
    productionId,
    isProductionSet,
    getStatus,
    goNext,
    mode = FormMode.CREATE,
    onStateChange,
    productionStatusCode = null,
    isUpdateNeeded = false,
    onUpdateNeededChange,
  },
  ref
) => {
  const defaultValues: DeliveryOptionsFormData = {
    action: FormAction.SAVE,
    rootPath: '',
    directory: '',
    archiveProduction: false,
    loadFilePathEnabled: false,
    filePath: 'Z:\\',
    volumeIncluded: false,
    volumeName: 'VOL00001',
    volumeLocation: '',
    nativeFolder: '',
    pdfFolder: '',
    textFolder: '',
    mp3Folder: '',
    imagesFolder: '',
    loadFilesFolder: '',
    translationsFolder: '',
    splitSubFolders: false,
    splitCount: '0',
  };

  const selectedSecurityGroupId = useSelector(
    (state: RootState) => state.production.securityGroupId
  );

  // Determine moduleType from isProductionSet using enum
  const moduleType = useMemo(
    () => (isProductionSet ? ModuleType.PRODUCTION : ModuleType.EXPORT),
    [isProductionSet]
  );

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    reset,
    formState: {errors, isDirty},
    watch,
  } = useForm<DeliveryOptionsFormData>({
    mode: 'onSubmit',
    resolver: zodResolver(deliveryOptionsSchema(moduleType)),
    defaultValues,
  });

  const [rootPathOptions, setRootPathOptions] = useState<ISelectItem[]>([]);
  const rootPathMapRef = useRef<Record<string, string>>({});
  const [volumeLocationOptions, setVolumeLocationOptions] = useState<
    ISelectItem[]
  >([]);
  const [folderModalState, setFolderModalState] = useState<FolderModalState>({
    show: false,
    message: '',
    context: null,
  });
  const [showArchiveWarning, setShowArchiveWarning] = useState(false);
  const [showRootPathError, setShowRootPathError] = useState(false);
  const [pendingSubmission, setPendingSubmission] =
    useState<SubmissionContext | null>(null);

  const loadFilePathEnabled = watch('loadFilePathEnabled');
  const volumeIncluded = watch('volumeIncluded');
  const splitSubFolders = watch('splitSubFolders');
  const rootPathValue = watch('rootPath');
  const volumeLocationValue = watch('volumeLocation');

  const isInProgressOrCompleted =
    productionStatusCode === ProductionStatus.COMPLETED ||
    productionStatusCode == ProductionStatus.INPROGRESS;
  const modeVal = isInProgressOrCompleted ? FormMode.VIEW : mode;
  const isFieldDisabled = modeVal === FormMode.VIEW;
  const dispatch = useDispatch();

  useEffect(() => {
    if (isDirty) {
      dispatch(setIsFieldsChanged({isFieldsChanged: true}));
    }
  }, [isDirty]);

  const rootPathDropdownLabel = t(
    'productionsAndExports.createNewProduction.deliveryOptions.rootPathLabel',
    {
      defaultValue: t(
        'productionsAndExports.createNewProduction.deliveryOptions.rootPath'
      ),
    }
  );
  const volumeLocationDropdownLabel = t(
    'productionsAndExports.createNewProduction.deliveryOptions.volumeLocationLabel',
    {
      defaultValue: t(
        'productionsAndExports.createNewProduction.deliveryOptions.volumeLocation'
      ),
    }
  );
  const filePathLabel = t(
    'productionsAndExports.createNewProduction.deliveryOptions.filePathLabel',
    {
      defaultValue: t(
        'productionsAndExports.createNewProduction.deliveryOptions.filePath'
      ),
    }
  );
  const splitCountLabel = t(
    'productionsAndExports.createNewProduction.deliveryOptions.splitCountLabel',
    {
      defaultValue: t(
        'productionsAndExports.createNewProduction.deliveryOptions.splitCount'
      ),
    }
  );
  const createFolderTitle = t(
    'productionsAndExports.createNewProduction.deliveryOptions.createFolderTitle'
  );
  const createFolderDescription = t(
    'productionsAndExports.createNewProduction.deliveryOptions.createFolderDescription'
  );
  const createFolderConfirm = t(
    'productionsAndExports.createNewProduction.deliveryOptions.createFolderConfirm',
    {defaultValue: 'Yes'}
  );
  const createFolderCancel = t(
    'productionsAndExports.createNewProduction.deliveryOptions.createFolderCancel',
    {defaultValue: 'No'}
  );

  useEffect(() => {
    register('rootPath');
  }, [register]);

  const {data: productLocationData, isLoading: isProductLocationLoading} =
    useGetProductLocationView(
      {
        SecurityGroupId: selectedSecurityGroupId ?? 0,
        productionId,
        moduleType: moduleType,
      },
      Boolean(selectedSecurityGroupId && productionId)
    );
  const validateDestinationMutation = useValidateDestination();
  const createDestinationMutation = useCreateDestination();
  const saveProductionLocationMutation = useSaveProductionLocation();

  useEffect(() => {
    if (!productLocationData) return;

    const rootPaths = productLocationData.lstProductionRootPath ?? [];
    const availableRootPaths: ISelectItem[] = rootPaths.map((item) => ({
      id: String(item.productionRootId),
      name: item.productionRootLabel ?? '',
    }));

    const rootMap: Record<string, string> = {};
    availableRootPaths.forEach((option: ISelectItem) => {
      const idStr = String(option.id ?? '');
      if (idStr && idStr !== '') {
        rootMap[idStr] = String(option.name ?? '');
      }
    });
    rootPathMapRef.current = rootMap;

    const rootOptionsWithPlaceholder: ISelectItem[] = [
      {
        id: '',
        name: t(
          'productionsAndExports.createNewProduction.deliveryOptions.rootPathPlaceholder'
        ),
      },
      ...availableRootPaths,
    ];
    setRootPathOptions(rootOptionsWithPlaceholder);

    const volumeEntries = productLocationData.volumenLocation
      ? (Object.entries(productLocationData.volumenLocation) as [
          string,
          string | null,
        ][])
      : [];
    const volumeOptions: ISelectItem[] = volumeEntries.map(([key, value]) => ({
      id: key,
      name: value ?? key,
    }));
    setVolumeLocationOptions(volumeOptions);

    const output = productLocationData.productionOutputLocation;
    const folders = productLocationData.productionComponentsFolderDetails;

    const resolvedRootPathId =
      output?.productionRootPathID !== undefined &&
      output?.productionRootPathID !== null
        ? String(output.productionRootPathID)
        : '';

    const defaultVolumeLocationId =
      volumeOptions.length > 0 &&
      volumeOptions[0].id !== undefined &&
      volumeOptions[0].id !== null
        ? String(volumeOptions[0].id)
        : '';
    const resolvedVolumeLocation =
      output?.isVolumeIncluded && output?.volumeLocationID
        ? String(output.volumeLocationID)
        : defaultVolumeLocationId;

    // Ensure default values are set if backend doesn't provide them
    // Always set defaults so they're available when toggles are turned on
    reset({
      action: FormAction.SAVE,
      rootPath: resolvedRootPathId,
      directory: output?.productionDirectory ?? '',
      archiveProduction: output?.shouldArchive ?? false,
      loadFilePathEnabled: output?.shouldIncludeDrive ?? false,
      filePath: output?.driveText || 'Z:\\',
      volumeIncluded: output?.isVolumeIncluded ?? false,
      volumeName: output?.volumeName || 'VOL00001',
      volumeLocation: resolvedVolumeLocation,
      nativeFolder: folders?.folderName_Natives ?? '',
      pdfFolder: folders?.folderName_PDF ?? '',
      textFolder: folders?.folderName_Text ?? '',
      mp3Folder: folders?.folderName_MP3 ?? '',
      imagesFolder: folders?.folderName_Images ?? '',
      loadFilesFolder: folders?.folderName_LoadFiles ?? '',
      translationsFolder: folders?.folderName_Translations ?? '',
      splitSubFolders: folders?.toSplitSubFolders ?? false,
      splitCount: String(folders?.splitCount ?? 0),
    });
  }, [productLocationData, reset, t]);

  const selectedRootPath =
    rootPathOptions.find((option) => option.id === rootPathValue) ??
    rootPathOptions.find((option) => option.id === '') ??
    null;
  const selectedVolumeLocation =
    volumeLocationOptions.find((option) => option.id === volumeLocationValue) ??
    null;

  const isMutating =
    validateDestinationMutation.isPending ||
    createDestinationMutation.isPending ||
    saveProductionLocationMutation.isPending;

  const isSaveDisabled = mode === FormMode.VIEW || isMutating;
  const isNextDisabled = mode === FormMode.VIEW || isMutating;

  useEffect(() => {
    onStateChange?.({
      saveDisabled: isSaveDisabled,
      nextDisabled: isNextDisabled,
    });
  }, [isSaveDisabled, isNextDisabled, onStateChange]);

  // Set default volume location to first option if empty and options are available
  useEffect(() => {
    if (
      volumeLocationOptions.length > 0 &&
      (!volumeLocationValue || volumeLocationValue.trim() === '')
    ) {
      const firstOptionId = volumeLocationOptions[0]?.id;
      if (firstOptionId) {
        setValue('volumeLocation', String(firstOptionId), {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
    }
  }, [volumeLocationOptions, volumeLocationValue, setValue]);

  const handleToggleChange =
    (
      field: keyof DeliveryOptionsFormData,
      resetFields?: Array<keyof DeliveryOptionsFormData>
    ) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      setValue(field, checked, {shouldDirty: true});
      resetFields?.forEach((resetField) => {
        if (!checked) {
          // Don't clear the value, just clear errors
          clearErrors(resetField);
        } else {
          // When toggling back on, restore default values if field is empty
          const currentValue = watch(resetField);
          if (!currentValue || currentValue === '') {
            if (resetField === 'filePath') {
              setValue(resetField, 'Z:\\', {
                shouldDirty: true,
                shouldValidate: false,
              });
            } else if (resetField === 'volumeName') {
              setValue(resetField, 'VOL00001', {
                shouldDirty: true,
                shouldValidate: false,
              });
            } else if (resetField === 'splitCount') {
              setValue(resetField, '1000', {
                shouldDirty: true,
                shouldValidate: false,
              });
            }
          }
        }
      });
    };

  const buildSubmissionContext = (
    formData: DeliveryOptionsFormData
  ): SubmissionContext | null => {
    const directory = formData.directory?.trim() ?? '';
    if (!directory) {
      setError('directory', {
        type: 'manual',
        message:
          moduleType === ModuleType.EXPORT
            ? t(
                'productionsAndExports.createNewProduction.deliveryOptions.errors.exportDirectoryRequired'
              )
            : t(
                'productionsAndExports.createNewProduction.deliveryOptions.errors.directoryRequired'
              ),
      });
      return null;
    }

    if (!formData.rootPath || !formData.rootPath.trim()) {
      setShowRootPathError(true);
      return null;
    }

    let rootPathName = rootPathMapRef.current[formData.rootPath] || '';
    if (
      !rootPathName &&
      productLocationData?.productionOutputLocation?.productionRootPathName
    ) {
      rootPathName =
        productLocationData.productionOutputLocation.productionRootPathName ??
        '';
    }

    if (!rootPathName) {
      setShowRootPathError(true);
      return null;
    }

    return {
      formData: {
        ...formData,
        directory,
        filePath: formData.filePath?.trim() ?? '',
      },
      markComplete: formData.action === FormAction.NEXT,
      rootPathId: Number(formData.rootPath),
      rootPathName,
    };
  };

  const executeSave = async (context: SubmissionContext) => {
    try {
      if (!isUpdateNeeded) {
        goNext?.();
        return;
      }
      const parsedSplitCount = Number(context.formData.splitCount);
      const splitCountValue = Number.isNaN(parsedSplitCount)
        ? 0
        : parsedSplitCount;
      const shouldIncludeDrive = context.formData.loadFilePathEnabled;
      const isVolumeIncluded = context.formData.volumeIncluded;
      const isSplitSubFolders = context.formData.splitSubFolders;
      const safeFolderValue = (value: string | undefined) => value ?? '';

      // Build ProductionComponentsFolderDetails conditionally
      const productionComponentsFolderDetails: any = {
        FolderName_Natives: safeFolderValue(context.formData.nativeFolder),
        FolderName_PDF: safeFolderValue(context.formData.pdfFolder),
        FolderName_Text: safeFolderValue(context.formData.textFolder),
        FolderName_MP3: safeFolderValue(context.formData.mp3Folder),
        FolderName_Images: safeFolderValue(context.formData.imagesFolder),
        FolderName_LoadFiles: safeFolderValue(context.formData.loadFilesFolder),
        FolderName_Translations: safeFolderValue(
          context.formData.translationsFolder
        ),
        ToSplitSubFolders: isSplitSubFolders,
      };

      // Only include SplitCount if splitSubFolders is true
      if (isSplitSubFolders) {
        productionComponentsFolderDetails.SplitCount = splitCountValue;
      }

      const payload = {
        ProductionId: productionId,
        IsMarkComplete: context.markComplete,
        ModuleType: moduleType,
        ProductionOutputLocation: {
          ProductionRootPathID: context.rootPathId,
          ProductionDirectory: context.formData.directory,
          ShouldIncludeDrive: shouldIncludeDrive,
          driveText: shouldIncludeDrive ? context.formData.filePath || '' : '',
          IsVolumeIncluded: isVolumeIncluded,
          VolumeName: isVolumeIncluded ? context.formData.volumeName || '' : '',
          VolumeLocationID: isVolumeIncluded
            ? context.formData.volumeLocation || ''
            : '',
          ShouldArchive: context.formData.archiveProduction,
          ProductionRootPathName: context.rootPathName,
        },
        ProductionComponentsFolderDetails: productionComponentsFolderDetails,
        SecurityGroupId: selectedSecurityGroupId ?? 0,
      };

      const response =
        await saveProductionLocationMutation.mutateAsync(payload);
      const successMessage =
        (response as any)?.messageToShow ||
        response?.message ||
        t(
          'productionsAndExports.createNewProduction.deliveryOptions.saveSuccess'
        );

      getStatus(
        true,
        ToastType.Success,
        context.markComplete
          ? t(
              'productionsAndExports.createNewProduction.common.nextSuccessToast'
            )
          : successMessage
      );
      // Update parent state to track that update is needed after save (only for SAVE action)
      if (!context.markComplete) {
        onUpdateNeededChange?.(true);
      }
      if (context.markComplete) {
        goNext?.();
      }
    } catch (error) {
      getStatus(
        true,
        ToastType.Error,
        (error as Error)?.message ||
          t(
            'productionsAndExports.createNewProduction.deliveryOptions.saveError'
          )
      );
    }
    dispatch(setIsFieldsChanged({isFieldsChanged: false}));
  };

  const createDestinationAndSave = async (context: SubmissionContext) => {
    try {
      // Call createDestination first - if 200 OK, proceed to save
      await createDestinationMutation.mutateAsync({
        RootPath: context.rootPathName,
        DestinationFolder: context.formData.directory,
      });

      // After createDestination succeeds (200 OK), proceed to save
      await proceedAfterValidation(context);
    } catch (error) {
      getStatus(
        true,
        ToastType.Error,
        (error as Error)?.message ||
          t(
            'productionsAndExports.createNewProduction.deliveryOptions.createDestinationError'
          )
      );
    }
  };

  const proceedAfterValidation = async (context: SubmissionContext) => {
    if (!context.formData.archiveProduction && context.markComplete) {
      setPendingSubmission(context);
      setShowArchiveWarning(true);
      return;
    }

    setPendingSubmission(null);
    await executeSave(context);
  };

  const beginSubmission = async (formData: DeliveryOptionsFormData) => {
    const context = buildSubmissionContext(formData);
    if (!context) return;

    try {
      // Step 1: Validate destination
      const validationResult = await validateDestinationMutation.mutateAsync({
        rootPath: context.rootPathName,
      });

      if (!validationResult.success) {
        getStatus(
          true,
          ToastType.Error,
          validationResult.message ||
            t(
              'productionsAndExports.createNewProduction.deliveryOptions.validateError'
            )
        );
        return;
      }

      // Case 1: Root path doesn't exist (rootExists: false)
      // Response: {"success": true, "rootExists": false, "message": "The Production Root Path does not exist..."}
      const rootExists = (validationResult as any).rootExists;
      if (rootExists === false) {
        // Root path doesn't exist, show folder creation modal
        setFolderModalState({
          show: true,
          message: createFolderDescription,
          context,
        });
        return;
      }

      // Case 2: Root path exists (alreadyExists: true)
      // Response: {"success":true,"alreadyExists":true,"message":"Production root path exists."}
      // Flow: validateDestination → createDestination → saveProductionLocation
      if (validationResult.alreadyExists === true) {
        // Root path exists, call createDestination then saveProductionLocation
        await createDestinationAndSave(context);
        return;
      }

      // Case 3: alreadyExists is false (destination folder doesn't exist)
      if (validationResult.alreadyExists === false) {
        // Destination folder doesn't exist, show folder creation modal
        setFolderModalState({
          show: true,
          message: createFolderDescription,
          context,
        });
        return;
      }

      // Default: if validation passed, proceed to save
      await proceedAfterValidation(context);
    } catch (error) {
      getStatus(
        true,
        ToastType.Error,
        (error as Error)?.message ||
          t(
            'productionsAndExports.createNewProduction.deliveryOptions.validateError'
          )
      );
    }
  };

  const handleArchiveWarningConfirm = async () => {
    if (!pendingSubmission) {
      setShowArchiveWarning(false);
      return;
    }

    const context = pendingSubmission;
    setShowArchiveWarning(false);
    setPendingSubmission(null);
    await executeSave(context);
  };

  const handleArchiveWarningCancel = () => {
    setPendingSubmission(null);
    setShowArchiveWarning(false);
  };

  const handleFolderModalCancel = () => {
    setFolderModalState({show: false, message: '', context: null});
  };

  const handleFolderModalConfirm = async () => {
    if (!folderModalState.context) {
      handleFolderModalCancel();
      return;
    }

    try {
      const context = folderModalState.context;
      // Call createDestination - if 200 OK, proceed to save
      await createDestinationMutation.mutateAsync({
        RootPath: context.rootPathName,
        DestinationFolder: context.formData.directory,
      });

      setFolderModalState({show: false, message: '', context: null});
      // After createDestination succeeds (200 OK), proceed to save
      await proceedAfterValidation(context);
    } catch (error) {
      getStatus(
        true,
        ToastType.Error,
        (error as Error)?.message ||
          t(
            'productionsAndExports.createNewProduction.deliveryOptions.createDestinationError'
          )
      );
      setFolderModalState({show: false, message: '', context: null});
    }
  };

  const onSubmit = async (data: DeliveryOptionsFormData) => {
    await beginSubmission(data);
  };

  useImperativeHandle(ref, () => ({
    save: () => {
      setValue('action', FormAction.SAVE);
      handleSubmit(onSubmit)();
    },
    next: () => {
      if (isInProgressOrCompleted) {
        goNext?.();
        return;
      }
      setValue('action', FormAction.NEXT);
      handleSubmit(onSubmit)();
    },
  }));

  if (isProductLocationLoading && !productLocationData) {
    return <ArrLoadingSpinner />;
  }

  return (
    <>
      <Box
        className="new-production-form-section"
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        role="form"
        aria-label={t(
          'productionsAndExports.createNewProduction.deliveryOptions.header'
        )}
      >
        <Box className="new-production-form delivery-options">
          <Box className="new-production-form-header">
            <Typography
              className="typo-text subhead2_size"
              data-testid={`delivery-options-header`}
            >
              {t(
                'productionsAndExports.createNewProduction.deliveryOptions.header'
              )}
            </Typography>
          </Box>

          <Box className="new-production-form-body">
            <Box className="delivery-section">
              <Typography
                className="section-title typo-text subhead4_size"
                component="div"
                data-testid={`delivery-options-specify-location`}
              >
                {t(
                  'productionsAndExports.createNewProduction.deliveryOptions.specifyLocation'
                )}
              </Typography>
              <Box className="field-row">
                <Box className="field-item">
                  <Typography
                    className="typo-text body3_size label"
                    component="div"
                    data-testid={`delivery-options-root-path`}
                  >
                    {t(
                      'productionsAndExports.createNewProduction.deliveryOptions.rootPath'
                    )}
                  </Typography>
                  <ArrSingleSelectDropdown
                    dropdownLabel={rootPathDropdownLabel}
                    dropdownItems={rootPathOptions}
                    selectedValues={selectedRootPath}
                    onSelectionChange={(item: ISelectItem | null) => {
                      setValue('rootPath', String(item?.id ?? ''), {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                      clearErrors('rootPath');
                    }}
                    disabled={isFieldDisabled}
                    error={!!errors.rootPath}
                    sizeVariant="medium"
                    dataTestId="asd-lstProductionRootPaths"
                  />
                </Box>
                <Box className="field-item">
                  <Typography
                    className="typo-text body3_size label"
                    component="div"
                    data-testid={`delivery-options-directory`}
                  >
                    {t(
                      'productionsAndExports.createNewProduction.deliveryOptions.directory'
                    )}
                  </Typography>
                  <ArrTextField
                    placeholder=""
                    {...register('directory', {
                      onChange: () => clearErrors('directory'),
                    })}
                    value={watch('directory')}
                    state={
                      isFieldDisabled
                        ? 'inactive'
                        : errors.directory
                          ? 'error'
                          : 'enabled'
                    }
                    showErrorIcon={!!errors.directory}
                    supportText={errors.directory?.message || ''}
                    disabled={isFieldDisabled}
                    sizeVariant="medium"
                    id="ProductionOutputLocation_ProductionDirectory"
                    dataTestId="do-production-directory"
                  />
                </Box>
              </Box>
            </Box>

            <Box className="delivery-section">
              <Box className="toggle-row">
                <ArrToggle
                  checked={watch('archiveProduction')}
                  onChange={handleToggleChange('archiveProduction')}
                  disabled={isFieldDisabled}
                  dataTestId="chkShouldArchive"
                />
                <Box className="toggle-label-wrapper">
                  <Typography
                    className="toggle-label typo-text body3_size"
                    component="div"
                    data-testid={`delivery-options-archive-export`}
                  >
                    {moduleType === ModuleType.EXPORT
                      ? t(
                          'productionsAndExports.createNewProduction.deliveryOptions.archiveExport'
                        )
                      : t(
                          'productionsAndExports.createNewProduction.deliveryOptions.archiveProduction'
                        )}

                    <ArrTooltipWrapper
                      closeTooltipOnEscPress={true}
                      trigger={
                        <InfoOutlinedIcon
                          tabIndex={0}
                          className="delivery-info-icon"
                        />
                      }
                    >
                      {moduleType === ModuleType.EXPORT
                        ? t(
                            'productionsAndExports.createNewProduction.deliveryOptions.archiveExportTooltip'
                          )
                        : t(
                            'productionsAndExports.createNewProduction.deliveryOptions.archiveProductionTooltip'
                          )}
                    </ArrTooltipWrapper>
                  </Typography>
                  <span
                    className="typo-text body3_size archiveNote"
                    data-testid={`delivery-options-span`}
                  >
                    {moduleType === ModuleType.EXPORT
                      ? t(
                          'productionsAndExports.createNewProduction.deliveryOptions.archiveExportNote'
                        )
                      : t(
                          'productionsAndExports.createNewProduction.deliveryOptions.archiveProductionNote'
                        )}
                  </span>
                </Box>
              </Box>
            </Box>

            <Box className="delivery-section">
              <Box className="toggle-row">
                <ArrToggle
                  checked={loadFilePathEnabled}
                  onChange={handleToggleChange('loadFilePathEnabled', [
                    'filePath',
                  ])}
                  disabled={isFieldDisabled}
                  dataTestId="ProductionOutputLocation_ShouldIncludeDrive"
                />
                <Typography
                  className="toggle-label typo-text body3_size"
                  component="div"
                  data-testid={`delivery-options-load-file-path`}
                >
                  {t(
                    'productionsAndExports.createNewProduction.deliveryOptions.loadFilePath'
                  )}
                </Typography>
              </Box>
              <Box className="field-row">
                <Box className="field-item">
                  <Typography
                    className="typo-text body3_size label"
                    component="div"
                    data-testid={`delivery-options-typography-file-path`}
                  >
                    {filePathLabel}
                  </Typography>
                  <ArrTextField
                    placeholder={t(
                      'productionsAndExports.createNewProduction.deliveryOptions.filePathPlaceholder'
                    )}
                    {...register('filePath', {
                      onChange: () => clearErrors('filePath'),
                    })}
                    dataTestId="do-file-path"
                    value={watch('filePath') || ''}
                    id="ProductionOutputLocation_DriveText"
                    disabled={isFieldDisabled || !loadFilePathEnabled}
                    state={
                      !loadFilePathEnabled || isFieldDisabled
                        ? 'inactive'
                        : errors.filePath
                          ? 'error'
                          : 'enabled'
                    }
                    showErrorIcon={!!errors.filePath}
                    supportText={errors.filePath?.message || ''}
                    sizeVariant="medium"
                  />
                </Box>
              </Box>
            </Box>

            <Box className="delivery-section">
              <Box className="toggle-row">
                <ArrToggle
                  checked={volumeIncluded}
                  onChange={handleToggleChange('volumeIncluded', [
                    'volumeName',
                    'volumeLocation',
                  ])}
                  disabled={isFieldDisabled}
                  dataTestId="ProductionOutputLocation_IsVolumeIncluded"
                />
                <Typography
                  className="toggle-label typo-text body3_size"
                  component="div"
                  data-testid={`delivery-options-volume-included`}
                >
                  {t(
                    'productionsAndExports.createNewProduction.deliveryOptions.volumeIncluded'
                  )}
                </Typography>
              </Box>
              <Box className="field-row">
                <Box className="field-item">
                  <Typography
                    className="typo-text body3_size label"
                    component="div"
                    data-testid={`delivery-options-volume-name`}
                  >
                    {t(
                      'productionsAndExports.createNewProduction.deliveryOptions.volumeName'
                    )}
                  </Typography>
                  <ArrTextField
                    placeholder={t(
                      'productionsAndExports.createNewProduction.deliveryOptions.volumeNamePlaceholder'
                    )}
                    {...register('volumeName')}
                    value={watch('volumeName') || ''}
                    disabled={isFieldDisabled || !volumeIncluded}
                    state={
                      !volumeIncluded || isFieldDisabled
                        ? 'inactive'
                        : errors.volumeName
                          ? 'error'
                          : 'enabled'
                    }
                    showErrorIcon={!!errors.volumeName}
                    supportText={errors.volumeName?.message || ''}
                    sizeVariant="medium"
                    id="ProductionOutputLocation_VolumeName"
                    dataTestId="do-volume-name"
                  />
                </Box>
                <Box className="field-item">
                  <Typography
                    className="typo-text body3_size label"
                    component="div"
                    data-testid={`delivery-options-volume-location`}
                  >
                    {t(
                      'productionsAndExports.createNewProduction.deliveryOptions.volumeLocation'
                    )}
                  </Typography>
                  <ArrSingleSelectDropdown
                    dropdownLabel={volumeLocationDropdownLabel}
                    dropdownItems={volumeLocationOptions}
                    selectedValues={selectedVolumeLocation}
                    onSelectionChange={(item: ISelectItem | null) => {
                      setValue('volumeLocation', String(item?.id ?? ''), {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }}
                    disabled={isFieldDisabled || !volumeIncluded}
                    error={!!errors.volumeLocation}
                    errorText={errors.volumeLocation?.message || ''}
                    sizeVariant="medium"
                    dataTestId="asd-lstVolumeLocation"
                  />
                </Box>
              </Box>
            </Box>

            <Box className="delivery-section">
              <Typography
                className="section-title typo-text subhead4_size"
                component="div"
                data-testid={`delivery-options-component-folders`}
              >
                {t(
                  'productionsAndExports.createNewProduction.deliveryOptions.componentFolders'
                )}
              </Typography>
              <Box className="folder-grid">
                {(
                  [
                    {
                      field: 'nativeFolder',
                      label: 'nativeFolder',
                      apiField: 'Natives',
                    },
                    {
                      field: 'imagesFolder',
                      label: 'imagesFolder',
                      apiField: 'Images',
                    },
                    {field: 'pdfFolder', label: 'pdfFolder', apiField: 'PDF'},
                    {
                      field: 'loadFilesFolder',
                      label: 'loadFilesFolder',
                      apiField: 'LoadFiles',
                    },
                    {
                      field: 'textFolder',
                      label: 'textFolder',
                      apiField: 'Text',
                    },
                    {
                      field: 'translationsFolder',
                      label: 'translationsFolder',
                      apiField: 'Translations',
                    },
                    {field: 'mp3Folder', label: 'mp3Folder', apiField: 'MP3'},
                  ] as Array<{
                    field: keyof DeliveryOptionsFormData;
                    label: string;
                    apiField: string;
                  }>
                ).map(({field, label, apiField}, index) => (
                  <Box className="field-item" key={field as string}>
                    <Typography
                      className="typo-text body3_size label"
                      component="div"
                      data-testid={`delivery-options-typography-delivery-option-${index}`}
                    >
                      {t(
                        `productionsAndExports.createNewProduction.deliveryOptions.${label}`
                      )}
                    </Typography>
                    <ArrTextField
                      {...register(field as keyof DeliveryOptionsFormData)}
                      value={
                        watch(field as keyof DeliveryOptionsFormData) as string
                      }
                      placeholder={t(
                        `productionsAndExports.createNewProduction.deliveryOptions.${label}`
                      )}
                      disabled={isFieldDisabled}
                      state={isFieldDisabled ? 'inactive' : 'enabled'}
                      sizeVariant="medium"
                      id={`ProductionComponentsFolderDetails_FolderName_${apiField}`}
                      dataTestId="do-delivery-label"
                    />
                  </Box>
                ))}
              </Box>
            </Box>

            <Box className="delivery-section">
              <Box className="toggle-row">
                <ArrToggle
                  checked={splitSubFolders}
                  onChange={handleToggleChange('splitSubFolders', [
                    'splitCount',
                  ])}
                  disabled={isFieldDisabled}
                  dataTestId="ProductionComponentsFolderDetails_ToSplitSubFolders"
                />
                <Typography
                  className="toggle-label typo-text body3_size"
                  component="div"
                  data-testid={`delivery-options-split-sub-folders`}
                >
                  {t(
                    'productionsAndExports.createNewProduction.deliveryOptions.splitSubFolders'
                  )}
                </Typography>
              </Box>
              <Box className="field-row">
                <Box className="field-item">
                  <Typography
                    className="typo-text body3_size label"
                    component="div"
                    data-testid={`delivery-options-typography-split-count-label`}
                  >
                    {splitCountLabel}
                  </Typography>
                  <ArrTextField
                    placeholder={t(
                      'productionsAndExports.createNewProduction.deliveryOptions.splitCountPlaceholder'
                    )}
                    {...register('splitCount')}
                    value={watch('splitCount') || ''}
                    disabled={isFieldDisabled || !splitSubFolders}
                    state={
                      !splitSubFolders || isFieldDisabled
                        ? 'inactive'
                        : errors.splitCount
                          ? 'error'
                          : 'enabled'
                    }
                    showErrorIcon={!!errors.splitCount}
                    supportText={errors.splitCount?.message || ''}
                    sizeVariant="medium"
                    id="ProductionComponentsFolderDetails_SplitCount"
                    dataTestId="do-split-count"
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <ArrModal
        showModal={showArchiveWarning}
        showClose={false}
        onClose={handleArchiveWarningCancel}
        title={t(
          'productionsAndExports.createNewProduction.deliveryOptions.archiveWarning.title'
        )}
        description={t(
          moduleType === ModuleType.EXPORT
            ? 'productionsAndExports.createNewProduction.deliveryOptions.archiveWarning.exportDescription'
            : 'productionsAndExports.createNewProduction.deliveryOptions.archiveWarning.description'
        )}
        dataTestId="deliveryoptions-modal"
        buttons={[
          {
            id: 'archive-warning-cancel',
            label: t(
              'productionsAndExports.createNewProduction.deliveryOptions.archiveWarning.cancel'
            ),
            variant: 'secondary',
            onClick: handleArchiveWarningCancel,
            dataTestId: 'dom-cancel-btn',
          },
          {
            id: 'archive-warning-confirm',
            label: t(
              'productionsAndExports.createNewProduction.deliveryOptions.archiveWarning.confirm'
            ),
            variant: 'primary',
            onClick: handleArchiveWarningConfirm,
            dataTestId: 'dom-confirm-btn',
          },
        ]}
      />
      <ArrModal
        showModal={folderModalState.show}
        onClose={handleFolderModalCancel}
        headerTitle={createFolderTitle}
        description={folderModalState.message || createFolderDescription}
        dataTestId="folder-modal"
        buttons={[
          {
            id: 'create-folder-cancel',
            label: createFolderCancel,
            variant: 'secondary',
            onClick: handleFolderModalCancel,
            dataTestId: 'fm-cancel-btn',
          },
          {
            id: 'create-folder-confirm',
            label: createFolderConfirm,
            variant: 'primary',
            onClick: handleFolderModalConfirm,
            disabled: createDestinationMutation.isPending,
            dataTestId: 'fm-confirm-btn',
          },
        ]}
      />
      <ArrModal
        showModal={showRootPathError}
        showClose={true}
        onClose={() => setShowRootPathError(false)}
        title={t('common.error')}
        description={t(
          moduleType === ModuleType.EXPORT
            ? 'productionsAndExports.createNewProduction.deliveryOptions.errors.exportRootPathRequired'
            : 'productionsAndExports.createNewProduction.deliveryOptions.errors.rootPathRequired'
        )}
        dataTestId="root-path-error-modal"
        buttons={[
          {
            id: 'root-path-error-ok',
            label: t('common.ok'),
            variant: 'primary',
            onClick: () => setShowRootPathError(false),
            dataTestId: 'rpe-ok-btn',
          },
        ]}
      />
    </>
  );
};

const DeliveryOptions = forwardRef(DeliveryOptionsInner);

DeliveryOptions.displayName = 'DeliveryOptions';

export default DeliveryOptions;
